/**
 * The gallery's contents, read off the asset folders rather than listed by hand.
 *
 * Each category points at one directory under assets/artPortfolioAssets; whatever image
 * sits in that directory is in the gallery. Dropping a new photo into `photography/` puts
 * it on the page with no edit here — the only reason to touch this file afterwards is to
 * give that photo a caption.
 *
 * Category order is the order below, and that is what the masonry reads out in. Within a
 * category files come out in natural filename order.
 */

// Captions are [title] or [title, description]. Any file with no entry shows up
// uncaptioned, which is what makes the folder the source of truth.
const CATEGORIES = [
    {
        name: 'Illustration',
        // The regex is inlined in each call rather than shared through a constant:
        // webpack has to read these arguments statically at build time to know which
        // directory to bundle.
        files: require.context('../../assets/artPortfolioAssets/studioArt', false, /\.(jpe?g|png|webp)$/i),
        instagram: 'https://www.instagram.com/kaixiarts/',
        captions: {
            'jinx.jpg': ['Jinx', 'Jinx - Arcane, League of Legends (Digital)'],
            'fallenAngel.jpg': ['Fallen Angel', 'Recreation of lucifer from fallen angel (Digital)'],
            'love.jpg': ['Love', 'Exploration of texture and movement (Digital)'],
            'lydia.jpg': ['Lydia Cassidy', 'Art Study of Lydia Cassidy to capture likeness (Digital)'],
            'plates.jpg': ['Glass melted plates', 'Matching glass molded plate design (12inx12in)'],
            'mural.jpg': ['Timpanogos Highschool Mural', 'Acrylic Mural Painted in Janitor Hall (30ftx15ft)'],
            'mushroomForest.jpg': ['Mushroom Forest Background Pixelart', 'Commissioned pixelart background for a indie game (Digital)'],
            'pixelForest.jpg': ['Forest Background Pixelart', 'Commissioned pixelart background for a indie game (Digital)'],
            'mucca.jpg': ['Lust in the Style of Mucca', "Recreating Mucca's style with the concept of lust - AP 2D Art (Digital)"],
            'spiderman.jpg': ['Miles Morales', 'Character integration into landscape study (Digital)'],
            'storyboardArtStudy.jpg': ['Story Board Art Study', 'Story telling through multi-frame art study (Digital)'],
            'vampire.jpg': ['Lesbian Vampires', 'Exploration of color and multi-character interaction (Digital)'],
        },
    },
    {
        name: 'Graphic Design',
        files: require.context('../../assets/artPortfolioAssets/graphicDesign', false, /\.(jpe?g|png|webp)$/i),
        captions: {
            'cityOfBlanksCoverAlbum.jpg': ['Album Cover'],
            'cmushirt.png': ['CMU 2025 HCII REU Shirt', 'Printed Shirt Design for the 2025 HCII REU'],
            'raevensManor.jpg': ['Graphic Design Portfolio Cover'],
            'fridayNightShirt.png': ['2025 Spring Crimson Nights Shirt Design', 'Printed Shirt Design for the 2025 Spring Crimson Nights'],
            'doceode.jpg': ['Website Banner'],
            'paintnsip.jpg': ['Paint and Sip', 'Part of marketing materials for Paint and Sip run by UPC'],
            'TSABuisnessCards.jpg': ['TSA Business Cards', 'Part of marketing materials for TSA 2022 Graphic Design competition (Placed 4th Nationally)'],
            'structuralEngineeringPoster.jpg': ['TSA Structural Engineering Poster', 'Part of marketing materials for TSA 2022 Graphic Design competition (Placed 4th Nationally)'],
            'stembbw.JPG': ['FBLA Coloring Set', 'Part of marketing materials for FBLA 2023 Graphic Design competition (Placed 3rd Nationally)'],
            'stemcover.JPG': ['FBLA Folder Cover', 'Part of marketing materials for FBLA 2023 Graphic Design competition (Placed 3rd Nationally)'],
            'OSTEMPoster.jpg': ['OSTEM Poster', 'Advertising Poster for OSTEM club at University of Utah'],
            'unionAfterDark.jpeg': ['Union After Dark Poster', 'Part of marketing materials for union after dark run by UPC and Friday Night Hype'],
        },
    },
    {
        name: 'Fashion',
        files: require.context('../../assets/artPortfolioAssets/fashion', false, /\.(jpe?g|png|webp)$/i),
        // The whole show was shot by one photographer, so the credit is a category-wide
        // default rather than sixteen identical caption entries.
        defaultDescription: 'Photo Credit: Minh Vuong @mv.ography',
        // Filenames here are already the titles, near enough: ShowPeice3Front reads out
        // as "FIB Show Piece 3 Front". Anything not matching that shape keeps no title.
        deriveTitle: (file) => {
            const match = file.match(/^ShowPeice(\d+)(Back|Front)\./i);
            return match ? `FIB Show Piece ${match[1]} ${match[2]}` : undefined;
        },
    },
    {
        name: 'Photography',
        files: require.context('../../assets/artPortfolioAssets/photography', false, /\.(jpe?g|png|webp)$/i),
        instagram: 'https://www.instagram.com/kaixiflics/',
        captions: {
            'IMG_0174.jpg': ['Lydia Cassidy', 'Lydia Cassidy, Seattle, WA'],
            'IMG_0513.jpg': ['Nandhini', 'Nandhini, Salt Lake City, UT'],
            'control.jpg': ['Control Station', 'Florida, Kennedy Space Center'],
            'IMG_1419.jpg': ['Sophia Bao', 'Sophia, Provo, UT'],
            'IMG_1616.jpg': ['Sophia Bao', 'Sophia, Orem, UT'],
            'IMG_3187.jpg': ['Grand Staircase-Escalante', 'Grand Staircase-Escalante National Monument, UT'],
            'IMG_3250.jpg': ['Self Portrait', 'Orem, UT'],
            'IMG_3595.jpg': ['Capital Reef National Park', 'Capital Reef National Park, UT'],
            'IMG_4902.jpg': ['Stairs', 'Guilin, Guizhou, China'],
            'IMG_3853.jpg': ['Pride', 'Pittsburgh, PA'],
            'IMG_4572-2.jpg': ['Theatre', 'London Coliseum, London, UK'],
            'nokings.jpg': ['No Kings', 'Pittsburgh, PA'],
        },
    },
];

// require.context hands back keys like './jinx.jpg'.
const fileNameOf = (key) => key.replace(/^\.\//, '');

function imagesIn(category) {
    const { files, captions = {}, exclude = [], defaultDescription, deriveTitle } = category;

    return files
        .keys()
        .map(fileNameOf)
        .filter((file) => !exclude.includes(file))
        // Natural order, so a future ShowPeice10 lands after 9 rather than after 1.
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        .map((file) => {
            const [title, description] = captions[file] || [];
            return {
                url: files(`./${file}`),
                title: title || (deriveTitle ? deriveTitle(file) : undefined),
                description: description || defaultDescription,
            };
        });
}

const perCategory = CATEGORIES.map(imagesIn);

export const galleryImages = perCategory.flat();

/**
 * Where each category begins in that flat run. Nothing on the page is labelled, so this
 * is the only record of the boundaries — the radial nav aims at them by measuring the
 * tile at `startIndex` rather than by looking for a heading.
 *
 * Categories that turned out empty are dropped: an emptied asset folder should take its
 * spoke off the wheel, not leave one pointing nowhere.
 */
export const gallerySections = CATEGORIES.map((category, index) => ({
    name: category.name,
    startIndex: perCategory.slice(0, index).reduce((total, list) => total + list.length, 0),
})).filter((_, index) => perCategory[index].length > 0);

export const instagramHandles = CATEGORIES.map((c) => c.instagram).filter(Boolean);
